import React, { useEffect, useState } from "react";
import { Box } from "@mui/material";
import dayjs from "dayjs";
import LGCrud from "../LGCrud";
import LGSelectCellRenderer from "../LGCrud/LGSelectCellRenderer";
import LGSelectCellEditor from "../LGCrud/LGSelectCellEditor";
import {
  createRaPayment,
  deleteRaPayment,
  getRaPaymentList,
  updateRaPayment,
} from "../../api/raPayments";
import { getPaymentConditionList } from "../../api/paymentConditions";
import { currencyFormatterMXN } from "../../services/valueFormatters";
import { useCompany } from "../../contexts/CompanyContext";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";

const toDateOnly = (value) => {
  if (!value) return null;
  const date = dayjs(value);
  return date.isValid() ? date.format("YYYY-MM-DD") : null;
};

const buildPayload = (data, { companyId, receiptAcknowledgementId, includeCreateFields }) => {
  const payload = {
    paymentConditionId: data.paymentCondition?.id ?? data.paymentConditionId ?? null,
    paymentDate: toDateOnly(data.paymentDate),
    amount: Number(data.amount) || 0,
  };

  if (includeCreateFields) {
    payload.companyId = companyId;
    payload.receiptAcknowledgementId = receiptAcknowledgementId;
    payload.installmentNo = Number(data.installmentNo) || 1;
  }

  return payload;
};

export default function RaPaymentTable({ props }) {
  const { id } = props;
  const { openSnack } = useSnack();
  const { selectedCompany } = useCompany();
  const [conditions, setConditions] = useState([]);
  const companyId = selectedCompany?.id ?? props?.data?.companyId;

  useEffect(() => {
    const fetchConditions = async () => {
      try {
        const response = await getPaymentConditionList();
        setConditions(Array.isArray(response.data) ? response.data : []);
      } catch (error) {
        openSnack(handleError(error), "error");
      }
    };
    fetchConditions();
  }, [openSnack]);

  const fetchPayments = async () => {
    if (!id) return { data: [] };
    const response = await getRaPaymentList({
      receiptAcknowledgementId: id,
      pageSize: 100,
    });
    const rows = Array.isArray(response.data)
      ? [...response.data].sort(
          (a, b) => (a.installmentNo || 0) - (b.installmentNo || 0)
        )
      : [];
    return { data: rows };
  };

  const handlePost = async (data) => {
    try {
      const payload = buildPayload(data, {
        companyId,
        receiptAcknowledgementId: id,
        includeCreateFields: true,
      });
      const response = await createRaPayment(payload);
      openSnack("Pago agregado exitosamente", "success");
      return response.data;
    } catch (error) {
      openSnack(handleError(error), "error");
      throw error;
    }
  };

  const handleUpdate = async (paymentId, data) => {
    try {
      const payload = buildPayload(data, { includeCreateFields: false });
      await updateRaPayment(paymentId, payload);
      openSnack("Pago actualizado exitosamente", "success");
    } catch (error) {
      openSnack(handleError(error), "error");
      throw error;
    }
  };

  const handleDelete = async (paymentId) => {
    await deleteRaPayment(paymentId);
  };

  if (!id) return null;

  return (
    <Box width="100%">
      <LGCrud
        clientSidePagination
        customFetchDeps={[id]}
        newRowDefaults={{
          installmentNo: 1,
          paymentCondition: null,
          paymentDate: null,
          amount: 0,
        }}
        dataGridProps={{ autoHeight: true }}
        columns={[
          {
            field: "installmentNo",
            headerName: "No.",
            flex: 0.15,
            editable: true,
            type: "number",
          },
          {
            field: "paymentCondition",
            headerName: "Condición de pago",
            flex: 0.35,
            editable: true,
            renderCell: (params) => (
              <LGSelectCellRenderer
                value={params.value}
                labelTemplate={(value) => value?.name || "Fecha fija"}
              />
            ),
            renderEditCell: (params) => (
              <LGSelectCellEditor
                {...params}
                dropdownData={conditions}
                labelTemplate={(value) => value?.name || ""}
              />
            ),
          },
          {
            field: "paymentDate",
            headerName: "Fecha de pago",
            flex: 0.25,
            editable: true,
            type: "date",
            valueGetter: (value) => (value ? dayjs(value).toDate() : null),
            valueFormatter: (value) =>
              value ? dayjs(value).format("DD/MM/YYYY") : "",
          },
          {
            field: "amount",
            headerName: "Monto",
            flex: 0.25,
            editable: true,
            type: "number",
            ...currencyFormatterMXN,
          },
        ]}
        actionHandlers={{
          // create: handlePost,
          update: handleUpdate,
          delete: handleDelete,
          fetchData: fetchPayments,
        }}
      />
    </Box>
  );
}
